import { h, createContext } from 'preact';
import { useEffect, useState, useMemo, useRef } from 'preact/hooks';
import useProductSiblings from 'utils/product-siblings';

const Context = createContext();

// Function to filter gift card images based on language
const filterGiftCardImages = (product, template) => {
  // Only apply filtering to gift cards with exactly 4 images
  if (template?.includes('gift-card') && product?.images?.length === 4) {
    const { locale } = Shopify;
    let filteredImages = [...product.images];
    let filteredMedia = [...product.media];

    if (locale === 'en') {
      filteredImages = product.images.slice(0, 2);
      filteredMedia = product.media.slice(0, 2);
    } else if (locale === 'fr') {
      filteredImages = product.images.slice(2, 4);
      filteredMedia = product.media.slice(2, 4);
    }

    return {
      ...product,
      images: filteredImages,
      media: filteredMedia,
    };
  }

  return product;
};

const ProductContext = ({ children, data }) => {
  const initialPageLoaded = useRef(false);
  const initialProduct = data?.product;
  const template = data?.template;

  if (!initialProduct) {
    // eslint-disable-next-line no-console
    console.error(
      'Theme Error: Please ensure this product is added to Online Store and Theme Access sales channels',
    );
    return {};
  }

  // Apply image filtering to the initial product
  const filteredInitialProduct = filterGiftCardImages(initialProduct, template);

  const hasOnlyDefaultVariant =
    filteredInitialProduct.hasOnlyDefaultVariant ||
    (filteredInitialProduct.options &&
      filteredInitialProduct.options.every(
        (option) => option.values?.length === 1,
      ));

  const initialVariant = hasOnlyDefaultVariant
    ? filteredInitialProduct?.variants[0]
    : null;

  const initialOptions =
    filteredInitialProduct.variantBySelectedOptions?.selectedOptions.map(
      (item) => ({
        ...item,
        value:
          filteredInitialProduct.variants.length > 1 &&
          item.name.trim().toLowerCase() ===
            window.TMW.Strings.product.options.size
            ? null
            : item.value,
      }),
    ) || [];

  const relatedProducts = useProductSiblings(filteredInitialProduct);

  const [products, setProducts] = useState([filteredInitialProduct]);
  const [currentProduct, setCurrentProduct] = useState(filteredInitialProduct);
  const [currentVariant, setCurrentVariant] = useState(initialVariant);
  const [options, setOptions] = useState(initialOptions);

  const fits = useMemo(
    () =>
      relatedProducts
        .reduce(
          (items, product) => [
            ...items,
            ...(product.options.find(
              (option) =>
                option.name.trim().toLowerCase() ===
                window.TMW.Strings.product.options.fit,
            )?.values || []),
          ],
          [],
        )
        .filter((value, index, array) => array.indexOf(value) === index)
        .sort()
        .reverse(),
    [relatedProducts],
  );

  // Wrapper function to filter images when setting a new product
  const setFilteredCurrentProduct = (product) => {
    const filteredProduct = filterGiftCardImages(product, template);
    setCurrentProduct(filteredProduct);
  };

  // Wrapper function to filter images when setting products array
  const setFilteredProducts = (productsArray) => {
    const filteredProductsArray = productsArray.map((product) =>
      filterGiftCardImages(product, template),
    );
    setProducts(filteredProductsArray);
  };

  const productService = {
    productState: {
      products,
      relatedProducts,
      currentProduct,
      currentVariant,
      options,
      fits,
    },
    productActions: {
      setCurrentProduct: setFilteredCurrentProduct,
      setCurrentVariant,
      setOptions,
      setProducts: setFilteredProducts,
    },
  };

  useEffect(() => {
    if (relatedProducts.length) {
      const fit = currentProduct.options.find(
        (option) =>
          option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.fit,
      )?.values[0];
      if (fit) {
        // Filter related products by fit
        const filteredProducts = relatedProducts.filter((item) =>
          item.options
            .find(
              (option) =>
                option.name.trim().toLowerCase() ===
                window.TMW.Strings.product.options.fit,
            )
            ?.values.includes(fit),
        );

        // Apply image filtering to each product
        const productsWithFilteredImages = filteredProducts.map((product) =>
          filterGiftCardImages(product, template),
        );

        setProducts(productsWithFilteredImages);
      } else {
        // Apply image filtering to all related products
        const productsWithFilteredImages = relatedProducts.map((product) =>
          filterGiftCardImages(product, template),
        );

        setProducts(productsWithFilteredImages);
      }
    }
  }, [relatedProducts]);

  useEffect(() => {
    const currentOptions = options.reduce(
      (items, item) => ({
        ...items,
        ...{
          [item.name]: item.value,
        },
      }),
      {},
    );
    setOptions([
      ...currentProduct.options.map((opt) => {
        let newValue = currentOptions[opt.name];
        if (!opt.values.includes(currentOptions[opt.name])) {
          newValue =
            opt.name.trim().toLowerCase() ===
              window.TMW.Strings.product.options.size && opt.values.length > 1
              ? null
              : opt.values[0];
        }
        return {
          name: opt.name,
          value: newValue,
        };
      }),
    ]);

    if (initialPageLoaded.current) {
      document.dispatchEvent(
        new CustomEvent('variant:change', { details: { currentProduct } }),
      );
    } else {
      const searchParams = new URLSearchParams(window.location.search);
      const urlVariantId = searchParams.get('variant');
      if (urlVariantId) {
        const variant = currentProduct.variants.find(
          (v) => v.id === `gid://shopify/ProductVariant/${urlVariantId}`,
        );
        if (variant) {
          setCurrentVariant(variant);
          setOptions(variant.selectedOptions);
        }
      }
    }
    initialPageLoaded.current = true;
  }, [currentProduct]);

  return (
    <Context.Provider value={{ ...productService, data }}>
      {children}
    </Context.Provider>
  );
};

export const withProductContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default ProductContext;
